import { DictTypes } from '@/shared/constants';
import type {
  DictExtrasTypes,
  GetDictContentType,
  GetDictContentWithExtrasType,
  TDict
} from '@/shared/types/dicts.types';

// Определение связанных справочников
const dependedDicts: Record<DictExtrasTypes, DictTypes[]> = {};
export const DEPENDED_DICTS_LIST = Object.entries(dependedDicts) as [
  keyof typeof dependedDicts,
  DictTypes[]
][];

// Справочники, которые имеют определенные запросы (не bulk)
// P.s. После изменения списка, нужно обновить файл src/store/dict.slice.ts
export const SPECIFIC_FETCH_DICT_TYPES = [DictTypes.Cities] as const;

export const PRELOAD_DICT_TYPES: DictTypes[] = Object.values(DictTypes);

export const mapContentItemsToGetByValue = <T extends DictTypes>(
  content: GetDictContentWithExtrasType<T>[]
) => {
  return content.reduce(
    (acc, item) => {
      acc[item.uuid] = item;
      if ('name' in item && item.name !== undefined && item.name !== null)
        acc[item.name] = item;
      if ('code' in item && item.code !== undefined && item.code !== null)
        acc[item.code] = item;
      if ('key' in item && item.key !== undefined && item.key !== null)
        acc[item.key] = item;
      return acc;
    },
    {} as Record<string | number, GetDictContentWithExtrasType<T> | undefined>
  );
};

export const dictContentMapper = (
  dictType: DictTypes,
  dictContentMap: Map<DictTypes, GetDictContentType<DictTypes>[]>,
  dicts: Partial<TDict>
) => {
  if (!dicts[dictType]) {
    dicts[dictType] = {
      content: [],
      elements: {}
    };
  }

  const getContent = <T extends DictTypes>(
    dictType: T
  ): GetDictContentType<T>[] => {
    return (dictContentMap.get(dictType) ?? []) as GetDictContentType<T>[];
  };

  (
    ({
      [DictTypes.Partners]: () => {
        const targetDict = dicts[DictTypes.Partners];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Partners).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.DocumentTypes]: () => {
        const targetDict = dicts[DictTypes.DocumentTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.DocumentTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.AutoTypes]: () => {
        const targetDict = dicts[DictTypes.AutoTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.AutoTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Brands]: () => {
        const targetDict = dicts[DictTypes.Brands];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Brands).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Cities]: () => {
        const targetDict = dicts[DictTypes.Cities];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Cities)
            .filter((city) => city.is_city)
            .sort((a, b) => a.name.localeCompare(b.name));
      },
      [DictTypes.Colors]: () => {
        const targetDict = dicts[DictTypes.Colors];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Colors).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.EngineTypes]: () => {
        const targetDict = dicts[DictTypes.EngineTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.EngineTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Models]: () => {
        const targetDict = dicts[DictTypes.Models];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Models).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Modules]: () => {
        const targetDict = dicts[DictTypes.Modules];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Modules).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Positions]: () => {
        const targetDict = dicts[DictTypes.Positions];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Positions).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.LeasingProducts]: () => {
        const targetDict = dicts[DictTypes.LeasingProducts];
        if (targetDict)
          targetDict.content = getContent(DictTypes.LeasingProducts).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.RepaymentTypes]: () => {
        const targetDict = dicts[DictTypes.RepaymentTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.RepaymentTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Statuses]: () => {
        const targetDict = dicts[DictTypes.Statuses];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Statuses).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Drive_Types]: () => {
        const targetDict = dicts[DictTypes.Drive_Types];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Drive_Types).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Gearbox_Types]: () => {
        const targetDict = dicts[DictTypes.Gearbox_Types];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Gearbox_Types).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Equipments_Types]: () => {
        const targetDict = dicts[DictTypes.Equipments_Types];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Equipments_Types).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.MaritalStatuses]: () => {
        const targetDict = dicts[DictTypes.MaritalStatuses];
        if (targetDict)
          targetDict.content = getContent(DictTypes.MaritalStatuses).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.SocialStatuses]: () => {
        const targetDict = dicts[DictTypes.SocialStatuses];
        if (targetDict)
          targetDict.content = getContent(DictTypes.SocialStatuses).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.OrganizationTypes]: () => {
        const targetDict = dicts[DictTypes.OrganizationTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.OrganizationTypes).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.Genders]: () => {
        const targetDict = dicts[DictTypes.Genders];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Genders).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.AddContactsIsRelated]: () => {
        const targetDict = dicts[DictTypes.AddContactsIsRelated];
        if (targetDict)
          targetDict.content = getContent(DictTypes.AddContactsIsRelated).sort(
            (a, b) => a.name.localeCompare(b.name)
          );
      },
      [DictTypes.SystemSettings]: () => {
        const targetDict = dicts[DictTypes.SystemSettings];
        if (targetDict)
          targetDict.content = getContent(DictTypes.SystemSettings);
      },
      [DictTypes.Branches]: () => {
        const targetDict = dicts[DictTypes.Branches];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Branches).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.ConditionTypes]: () => {
        const targetDict = dicts[DictTypes.ConditionTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.ConditionTypes);
      },
      [DictTypes.Countries]: () => {
        const targetDict = dicts[DictTypes.Countries];
        if (targetDict)
          targetDict.content = getContent(DictTypes.Countries).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.ProductTypes]: () => {
        const targetDict = dicts[DictTypes.ProductTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.ProductTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      },
      [DictTypes.ClientTypes]: () => {
        const targetDict = dicts[DictTypes.ClientTypes];
        if (targetDict)
          targetDict.content = getContent(DictTypes.ClientTypes).sort((a, b) =>
            a.name.localeCompare(b.name)
          );
      }
    }) satisfies Record<DictTypes, () => void>
  )[dictType]();
};
